import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from '../api.ts';
import { ActivationRuleSetting } from '../lib/activation-rule.tsx';
import { AddressAutocomplete } from '../lib/address-autocomplete.tsx';
import { cornerIndex, drawingShape } from '../lib/draft-shape.ts';
import {
  AddRow,
  FieldGroup,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FieldStatic,
  GroupRow,
  Help,
  Segmented,
  Switch,
} from '../lib/form.tsx';
import { frameFor } from '../lib/frame.ts';
import { OutlineControls } from '../lib/outline-controls.tsx';
import { reverseAddress } from '../lib/reverse-address.ts';
import { nearMe } from '../lib/my-location.ts';
import { addressSearchRegion, type SearchRegion } from '../lib/search-region.ts';
import { Map, type MapCircle, type MapPoint, type MapPolygon } from '../map.tsx';
import { useMapCanvas } from '../map-layer.tsx';
import { defaultCenter } from '../map-style.ts';
import {
  DuplicateSiteFinding,
  duplicateFromError,
  useSiteMatch,
  type DuplicateSite,
} from '../site-duplicate.tsx';
import { Button, Finding, Spinner } from '../ui.tsx';

/**
 * The one building-setup experience — a single full-screen, multi-step wizard,
 * reused **everywhere a building is added** (piece 2 of the dashboard IA
 * rework):
 *
 * - **"Add building"** from the Locations list opens it at its own route
 *   (`/locations/new`) — a whole building is not a slide-over, the same call the
 *   building *detail* made when it became a full page rather than a drawer (Trav
 *   2026-09-13). It replaces the old `InspectOverlay` add form.
 * - **The org setup flow** (`setup.tsx`) delegates its "add the first building"
 *   step to this same component, so there is one building-setup experience, not
 *   two inline forms drifting apart.
 *
 * It is **almost entirely client orchestration** over endpoints that already
 * exist — create site, assembly points, zones, ECO, teams, invites — so it
 * reuses the detail screen's `AddressAutocomplete` and the readiness `Eco`
 * board rather than reinventing them. The last step is the AUDIENCE, not a
 * roster manager: which team(s) an alert on this building reaches (migration
 * 0113) — bulk roster loading lives on the People screen.
 *
 * The shape (`docs/design-dashboard.md`): one clear step per screen, the primary
 * action bottom-anchored, and a disabled action greys and stays put. The step
 * that **matters** is the first — it creates the building — and it is the only
 * one that cannot be skipped; every step after it is genuinely skippable, so a
 * café adds one building and leaves while an enterprise walks all five.
 */

/**
 * The building is created at step 1 and the following steps attach to it, so the
 * wizard has two phases and the step id encodes which. `place` is pre-creation;
 * everything after it needs a real `siteId`.
 */
type WizardStep = 'place' | 'assembly' | 'zones' | 'wardens' | 'people';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'place', label: 'Place it' },
  { id: 'assembly', label: 'Assembly points' },
  { id: 'zones', label: 'Areas' },
  { id: 'wardens', label: 'Wardens' },
  { id: 'people', label: 'Who is alerted' },
];

/** The three kinds of assembly point migration 0030 gives a site — muster
 *  (outdoors), shelter (indoors) and reunification — offered as a `Select`. */
/**
 * The assembly step's three sections — one per kind migration 0030 names, each
 * with the sentence that says what it IS (owner direction 2026-09-21: "clearly
 * divided into 3 sections... each with a clear explanation"). Choosing the
 * wrong kind sends people — or their parents — to the wrong place, so the
 * explanation lives on the section, not in a select nobody reads.
 */
const ASSEMBLY_SECTIONS: {
  kind: api.AssemblyKind;
  title: string;
  explain: string;
  addLabel: string;
  addAnotherLabel: string;
  placeholder: string;
  /** What the pin reads as on the map until it is named. */
  newLabel: string;
}[] = [
  {
    kind: 'outdoor_assembly',
    title: 'Evacuation points',
    explain: 'Outdoors, clear of the building — where everyone goes during an evacuation.',
    addLabel: 'Add an evacuation point',
    addAnotherLabel: 'Add another evacuation point',
    placeholder: 'Front car park',
    newLabel: 'New evacuation point',
  },
  {
    kind: 'indoor_shelter',
    title: 'Lockdown points',
    explain: 'Inside the building — where people shelter, out of sight, during a lockdown.',
    addLabel: 'Add a lockdown point',
    addAnotherLabel: 'Add another lockdown point',
    placeholder: 'Library, Level 2',
    newLabel: 'New lockdown point',
  },
  {
    kind: 'reunification',
    title: 'Reunification points',
    explain: 'Where families collect people afterwards — kept apart from the headcount.',
    addLabel: 'Add a reunification point',
    addAnotherLabel: 'Add another reunification point',
    placeholder: 'School hall foyer',
    newLabel: 'New reunification point',
  },
];

/**
 * The assembly point being added or edited (one at a time). Held by the
 * wizard until Save, so the map draws it live and nothing half-made reaches
 * the server.
 */
interface PointDraft {
  /** null = a new point; otherwise the saved point being edited. */
  id: string | null;
  kind: api.AssemblyKind;
  name: string;
  at: api.LatLon | null;
  /** The address field's text — what the pin reads as, or what is being typed. */
  address: string;
  /** One shape per point (0123): a circle, a drawn outline, or (reunification only) none. */
  area: 'circle' | 'outline' | 'none';
  radiusM: number;
  ring: api.LatLon[];
}

/** Map ids for the draft point's pin, its ↔ handle and its outline. */
const DRAFT_PIN = 'draft-point';
const DRAFT_HANDLE = 'draft-handle';
const DRAFT_OUTLINE = 'draft-outline';
/** The building's outline while it is being drawn. */
const BUILDING_OUTLINE = 'wizard-outline-drawing';

/**
 * "How far across" as a slider AND a typed number driving the same value
 * (owner direction 2026-09-25: every geofence-radius ask is a slider as well
 * as text), wearing the form field: the label floats, the slider takes the
 * width, the number sits at the right with its unit. The slider covers the
 * range somebody eyeballs — drag to watch the circle grow on the map — and
 * the box takes any number the slider does not reach. Empty means none: no
 * circle, nothing stored.
 */
function AreaAcross({
  label,
  value,
  onChange,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** The slider's top in metres; the text box is unbounded past it. */
  max: number;
}) {
  const n = Number(value);
  const slider = value.trim() !== '' && Number.isFinite(n) ? Math.min(Math.max(n, 0), max) : 0;
  return (
    <div className="relative h-11 w-full rounded-[10px] border border-[var(--line-2)] bg-[var(--paper)]/80 has-focus-visible:border-[var(--focus)] has-focus-visible:ring-1 has-focus-visible:ring-[var(--focus)]">
      <FieldLabel>{label}</FieldLabel>
      <div className="absolute inset-x-3 top-[21px] bottom-0 flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={max}
          step={5}
          value={slider}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(v === 0 ? '' : String(v));
          }}
          aria-label={`${label} in metres`}
          className="h-1 min-w-0 flex-1 cursor-pointer accent-[var(--ink)] outline-none"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          placeholder="—"
          aria-label={`${label} in metres, typed`}
          className="h-[20px] w-[48px] bg-transparent text-right text-[14px] leading-[18px] text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]"
        />
        <span className="text-[12px] leading-[16px] text-[var(--ink-2)]">m</span>
      </div>
    </div>
  );
}

function diameterToRadius(v: string): number | undefined {
  const n = Number(v);
  if (v.trim() === '' || !Number.isFinite(n)) return undefined;
  const r = Math.round(n / 2);
  return r >= 5 && r <= 5000 ? r : undefined;
}

/**
 * The area a new evacuation point starts with — 40 m across. Trav 2026-09-29:
 * "when a pin is on the map it should show the geofence boundary". A point with
 * no area never auto-marks anyone safe, so an evacuation point that starts
 * without one is the quiet failure; starting with a modest one, grabbed and
 * resized on the map, is the honest default.
 */
const DEFAULT_EVAC_RADIUS_M = 20;

/** Metres between two coordinates (haversine) — the resize handle's radius. */
function metresBetween(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** The point `radiusM` due east of a centre — where the resize handle sits. */
function eastOf(
  c: { latitude: number; longitude: number },
  radiusM: number,
): { latitude: number; longitude: number } {
  const dLon = radiusM / (111_320 * Math.cos((c.latitude * Math.PI) / 180));
  return { latitude: c.latitude, longitude: c.longitude + dLon };
}

/**
 * A refusal in words (item 3: "It's a different building" did nothing — a
 * refusal must never be silent). A stale session is the common one: the
 * override is an admin act behind the freshness gate.
 */
function sayRefusal(e: Error, doing: string): string {
  const code = (e as { detail?: { code?: unknown } }).detail?.code;
  if (code === 'stale_session') return `Sign in again to ${doing} — it needs a recent sign-in.`;
  return e.message || `Couldn't ${doing}. Try again.`;
}

/** Metres between two pins, for "is an existing building sitting on the draft". */
const NEAR_DRAFT_M = 10;

/** A radius the schema accepts (CHECK 5..5000), whole metres. */
function clampRadius(m: number): number {
  return Math.min(5000, Math.max(5, Math.round(m)));
}

export function BuildingWizard({
  orgId,
  onComplete,
  onExit,
  embedded = false,
  presenceDefault,
}: {
  orgId: string;
  /**
   * The building is set up and the operator is done — either they reached the
   * end or skipped to it. Carries the new building's id so the caller can land
   * them on the full-page building detail (`/locations/<siteId>`), where they
   * see what they built and the readiness gaps for whatever they skipped.
   */
  onComplete: (siteId: string) => void;
  /**
   * Leave the wizard **before** a building exists (the step-1 "Cancel" way out).
   * The standalone route returns to the buildings list; setup owns its own way
   * out, so it passes none and the control is hidden. Never offered once the
   * building is created — from there the way forward is the building, via
   * `onComplete`.
   */
  onExit?: () => void;
  /**
   * Rendered inside the org setup flow rather than standalone. Suppresses this
   * component's own `PageTitle` (setup owns the "Set up <org>" heading, and a
   * second `<h1>` would break heading order), the pre-creation Cancel (setup
   * owns "Skip setup") and this wizard's own `Stepper` (setup renders the outer
   * "Shape · Organisation · Building · Review" stepper, and two stacked rows of
   * pills read as duplicated chrome — board MEDIUM). The steps and the create
   * are identical, which is the whole point: one experience, not two.
   */
  embedded?: boolean;
  /**
   * The presence default to create the building with (`presence_enabled`,
   * migration 0081) — counted presence vs roster-based, which decides the
   * accountability denominator. Setup passes it from the chosen shape
   * (`cfg.presenceDefault`: off for a café/tenant, on for a whole building or
   * campus) so a building created here matches the shape the operator picked;
   * the standalone add-building route leaves it undefined and takes the server
   * default (counted). Carried, never a new control — it is a default, not a
   * question, per the wizard's one-question-per-screen shape.
   */
  presenceDefault?: boolean;
}) {
  // The building being set up. Empty until step 1 creates it; once set, every
  // later step attaches to it and the stepper unlocks. This is the wizard's one
  // piece of hard state — everything else each step reads live from the server,
  // so re-entering a step never disagrees with what exists.
  const [siteId, setSiteId] = useState('');
  const [siteName, setSiteName] = useState('');
  // The created building's address on file — the "Place it" screen shows it in
  // a field the pin's reading writes into after a drag.
  const [siteAddress, setSiteAddress] = useState('');
  // The building's coordinate, captured at creation so the assembly-point step
  // can default its lat/lng near the building — a café then only has to name
  // "Front car park" rather than hunt for a coordinate.
  const [siteCoord, setSiteCoord] = useState<{ latitude: number; longitude: number } | null>(null);

  // ---- the wizard map's state (piece 2b), lifted here so ONE persistent map
  // on the right serves every step instead of a preview appearing mid-form
  // (the reflow #471 removed). The steps read and write it; the map renders it.
  //
  // Pre-creation the building's pin is `draftCoord` — set by picking an address
  // suggestion OR by tapping the map. `boundaryDiameter` is the "how far
  // across" answer, drawn live as the geofence circle and halved into
  // `geofence_radius_m` on create (#472).
  const [draftCoord, setDraftCoord] = useState<{ latitude: number; longitude: number } | null>(
    null,
  );
  const [boundaryDiameter, setBoundaryDiameter] = useState('');
  // The building's pin under the finger (MapKit drag) — the boundary circle
  // follows it live; the drop commits it (Trav, 2026-09-30: a correct address
  // whose geocoded point is not the building's middle left the circle off).
  const [buildingDrag, setBuildingDrag] = useState<api.LatLon | null>(null);
  // The address the pin now reads as, after a drop or a tap — the place step
  // writes it into its address field. `n` makes a repeat reading a new event.
  const [pinAddress, setPinAddress] = useState<{ text: string; n: number } | null>(null);
  // The building's drawn outline (migration 0140), committed and in progress.
  // Either replaces the circle — one building, one boundary shape.
  const [boundaryRing, setBoundaryRing] = useState<api.LatLon[] | null>(null);
  const [drawingBoundary, setDrawingBoundary] = useState<api.LatLon[] | null>(null);
  // Bumped when the camera SHOULD re-frame: an address picked, a pin placed by
  // tap, a point moved by address. Never by a drag.
  const [frameTick, setFrameTick] = useState(0);
  // The assembly point being added or edited — ONE at a time (Trav,
  // 2026-10-01: "give it a name, then either type an address or tap on the
  // map, then draw a polygon or create a bubble around the point, then save it
  // and there is another button asking if you want to add another one"). It
  // lives here until Save, so nothing half-made reaches the server and the map
  // draws it straight from this state — a drag or a resize follows the finger.
  const [draft, setDraft] = useState<PointDraft | null>(null);
  // The ↔ handle under the finger mid-drag: it follows the finger, and is
  // never pulled back due east of the centre while it moves.
  const [handleAt, setHandleAt] = useState<api.LatLon | null>(null);
  const [pointError, setPointError] = useState<string | null>(null);

  const [current, setCurrent] = useState<WizardStep>('place');
  useEffect(() => {
    if (current !== 'assembly') {
      setDraft(null);
      setPointError(null);
    }
  }, [current]);
  const idx = STEPS.findIndex((s) => s.id === current);

  // The created site's assembly points — the same queryKey the assembly step
  // uses, so TanStack shares one fetch between the form and the map.
  const points = useQuery({
    queryKey: ['points', orgId, siteId],
    queryFn: () => api.getAssemblyPoints(orgId, siteId),
    enabled: Boolean(siteId),
  });
  const qc = useQueryClient();
  const refreshPoints = () => void qc.invalidateQueries({ queryKey: ['points', orgId, siteId] });

  // The org's buildings: where address search should look first (item 1) and
  // which existing pins sit on top of the draft (item 4).
  const orgSites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });
  // No country is preferred (Trav 2026-09-30, "launching globally"): the
  // org's own buildings, else near the person (their time zone's city, or what
  // they shared with "Use my location"), else no hint.
  const addressRegion = useMemo(
    () => addressSearchRegion({ buildings: orgSites.data?.sites ?? [], near: nearMe() }),
    [orgSites.data],
  );
  // The assembly points' search looks around the building just placed first
  // (Trav, 2026-10-01: an address near a Melbourne building came back as UK
  // streets — that field searched with no hint at all). A sensitive building
  // never shapes the query, so it falls back to the org's other buildings.
  const createdSensitive = orgSites.data?.sites.find((st) => st.id === siteId)?.sensitive ?? false;
  const assemblyRegion = useMemo(
    () =>
      addressSearchRegion({
        anchor: siteCoord ? { ...siteCoord, sensitive: createdSensitive } : null,
        buildings: orgSites.data?.sites ?? [],
        near: nearMe(),
      }),
    [siteCoord, createdSensitive, orgSites.data],
  );

  // Where the pin now reads as — looked up after a tap or a drop, written into
  // the place step's address field before creation and shown beside the pin
  // after it. Display only; the address on file changes only when asked.
  // Numbered: two quick drops resolve in any order, and only the latest
  // reading may land (the autocomplete lesson).
  const readSeq = useRef(0);
  const readPin = (at: api.LatLon) => {
    const mine = ++readSeq.current;
    void reverseAddress(at.latitude, at.longitude).then((text) => {
      if (mine !== readSeq.current) return;
      if (text) setPinAddress((prev) => ({ text, n: (prev?.n ?? 0) + 1 }));
    });
  };

  // Moving the CREATED building (the pin dragged or tapped while "Place it" is
  // open again): a location-changing update, so the server's one-building-
  // one-site check runs (409 site_duplicate) and "It's a different building"
  // is its audited override.
  const [moveDuplicate, setMoveDuplicate] = useState<{
    site: DuplicateSite;
    at: api.LatLon;
  } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const moveBuilding = useMutation({
    mutationFn: (v: { at: api.LatLon; confirmDistinct?: boolean }) =>
      api.updateSite(orgId, siteId, {
        latitude: v.at.latitude,
        longitude: v.at.longitude,
        ...(v.confirmDistinct ? { confirmDistinct: true } : {}),
      }),
    onSuccess: (_r, v) => {
      setSiteCoord(v.at);
      setMoveDuplicate(null);
      setMoveError(null);
      setBuildingDrag(null);
      readPin(v.at);
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: Error, v) => {
      setBuildingDrag(null);
      const dup = duplicateFromError(e);
      setMoveDuplicate(dup ? { site: dup, at: v.at } : null);
      setMoveError(dup ? null : sayRefusal(e, 'move the building'));
    },
  });
  const placeBuilding = (at: api.LatLon) => {
    if (siteId) {
      moveBuilding.mutate({ at });
      return;
    }
    setDraftCoord(at);
    readPin(at);
  };
  // Where the draft's pin reads as, written into its address field after a tap
  // or a drop. Numbered, so only the latest reading lands; nothing found reads
  // as an empty field rather than the address of where the pin USED to be.
  const draftSeq = useRef(0);
  const readDraft = (at: api.LatLon) => {
    const mine = ++draftSeq.current;
    void reverseAddress(at.latitude, at.longitude).then((text) => {
      if (mine !== draftSeq.current) return;
      setDraft((d) => (d ? { ...d, address: text ?? '' } : d));
    });
  };
  // Move the draft's pin: a drawn outline travels with it (as the building's
  // does), so the pin never ends up outside its own area.
  const moveDraft = (at: api.LatLon, opts: { readAddress: boolean }) => {
    setDraft((d) => {
      if (!d) return d;
      const ring =
        d.at && d.ring.length > 0
          ? d.ring.map((c) => ({
              latitude: c.latitude + (at.latitude - d.at!.latitude),
              longitude: c.longitude + (at.longitude - d.at!.longitude),
            }))
          : d.ring;
      return { ...d, at, ring };
    });
    if (opts.readAddress) readDraft(at);
  };

  /** Start a new point of this kind — or edit a saved one. */
  const openDraft = (kind: api.AssemblyKind, point?: api.AssemblyPoint) => {
    setPointError(null);
    setHandleAt(null);
    draftSeq.current += 1;
    if (point) {
      const at = { latitude: point.latitude, longitude: point.longitude };
      setDraft({
        id: point.id,
        kind: point.kind,
        name: point.name,
        at,
        address: '',
        area:
          point.area_polygon !== null
            ? 'outline'
            : point.radius_m !== null
              ? 'circle'
              : point.kind === 'outdoor_assembly'
                ? 'circle'
                : 'none',
        radiusM: point.radius_m ?? DEFAULT_EVAC_RADIUS_M,
        ring: point.area_polygon ?? [],
      });
      if (point.kind !== 'indoor_shelter') readDraft(at);
      return;
    }
    // A lockdown point is a place INSIDE the building: its pin starts on the
    // building, and is dragged to the spot.
    const building = siteCoord ?? draftCoord;
    setDraft({
      id: null,
      kind,
      name: '',
      at: kind === 'indoor_shelter' && building ? building : null,
      address: '',
      area: kind === 'outdoor_assembly' ? 'circle' : 'none',
      radiusM: DEFAULT_EVAC_RADIUS_M,
      ring: [],
    });
  };

  const savePoint = useMutation({
    mutationFn: async (d: PointDraft) => {
      if (!d.at) throw new Error('Place the point on the map first.');
      const name = d.name.trim();
      const indoor = d.kind === 'indoor_shelter';
      const radiusM = !indoor && d.area === 'circle' ? clampRadius(d.radiusM) : undefined;
      const ring = !indoor && d.area === 'outline' ? d.ring : undefined;
      if (d.id) {
        return api.updateAssemblyPoint(orgId, siteId, d.id, {
          name,
          latitude: d.at.latitude,
          longitude: d.at.longitude,
          ...(indoor
            ? {}
            : radiusM !== undefined
              ? { radiusM }
              : ring !== undefined
                ? { areaPolygon: ring }
                : { radiusM: null, areaPolygon: null }),
        });
      }
      return api.addAssemblyPoint(
        orgId,
        siteId,
        name,
        d.at.latitude,
        d.at.longitude,
        d.kind,
        radiusM,
        ring,
      );
    },
    onSuccess: () => {
      refreshPoints();
      setDraft(null);
      setHandleAt(null);
      setPointError(null);
    },
    // A refusal (a degenerate outline, a stale session) keeps the draft so it
    // can be fixed — with the server's sentence on screen, never a silent button.
    onError: (e: Error) => setPointError(sayRefusal(e, 'save this point')),
  });
  const deletePoint = useMutation({
    mutationFn: (id: string) => api.removeAssemblyPoint(orgId, siteId, id),
    onSuccess: (_r, id) => {
      refreshPoints();
      setDraft((d) => (d?.id === id ? null : d));
      setPointError(null);
    },
    onError: (e: Error) => setPointError(sayRefusal(e, 'remove this point')),
  });
  const goTo = (id: WizardStep) => setCurrent(id);
  const next = () => {
    const n = STEPS[idx + 1];
    if (n) setCurrent(n.id);
    else onComplete(siteId);
  };
  const back = () => {
    const p = STEPS[idx - 1];
    if (p) setCurrent(p.id);
  };

  // Once the building exists, the way out is the building itself — "skip to the
  // end" completes with the gaps for whatever was skipped, exactly like reaching
  // the last step.
  const finish = () => onComplete(siteId);

  // Standalone, "done" lands the operator on the new building's page; embedded
  // in setup, it hands control back to the setup flow (its review step), so the
  // labels do not promise a navigation that will not happen.
  const skipLabel = embedded ? 'Skip the rest' : 'Skip to the building';
  const finishLabel = embedded ? 'Finish' : 'Finish — go to the building';

  // ---- what the wizard's map draws, derived from the step state above.
  //
  // Memoised on the real inputs so the substrate registration effect below has
  // an honest dependency: pushing a fresh object into the substrate on every
  // render would re-render the whole shell in a loop.
  // Committed position (camera, create) vs drawn position (follows a drag).
  const buildingCommitted = siteCoord ?? draftCoord;
  const buildingAt = buildingDrag ?? buildingCommitted;
  const assemblyList = points.data?.assemblyPoints;
  const stage = useMemo<{
    focusAt: { latitude: number; longitude: number } | null;
    points: MapPoint[];
    circles: MapCircle[];
    polygons: MapPolygon[];
  }>(() => {
    const placingBuilding = current === 'place';
    // The saved points, faded while another is being worked on. The one being
    // edited is drawn from the draft instead. A lockdown point made before they
    // had their own pin sits exactly on the building — it is not drawn as a
    // second pin stacked on it.
    const saved = (assemblyList ?? []).filter(
      (p) =>
        p.id !== draft?.id &&
        !(
          p.kind === 'indoor_shelter' &&
          buildingCommitted &&
          metresBetween(p, buildingCommitted) < 1
        ),
    );
    const dim = draft !== null;
    const indoorDraft = draft?.kind === 'indoor_shelter';
    const draftCircle =
      draft?.at && !indoorDraft && draft.area === 'circle'
        ? { at: draft.at, radiusM: clampRadius(draft.radiusM) }
        : null;
    const draftOutline =
      draft && !indoorDraft && draft.area === 'outline'
        ? drawingShape(draft.ring, DRAFT_OUTLINE, 'assembly', true)
        : null;
    const outlineDrawing = drawingBoundary
      ? drawingShape(drawingBoundary, BUILDING_OUTLINE, 'boundary', true)
      : null;
    const boundaryRadius = diameterToRadius(boundaryDiameter);
    const section = draft ? ASSEMBLY_SECTIONS.find((s) => s.kind === draft.kind) : undefined;
    return {
      focusAt: buildingAt,
      points: [
        // Drawn by the wizard while it is a draft, and again while "Place it"
        // is open after creation (the estate copy is hidden then — one pin per
        // building), so it can be dragged: the drop is the correction.
        ...(buildingAt && (!siteId || placingBuilding)
          ? [
              {
                id: 'wizard-site',
                latitude: buildingAt.latitude,
                longitude: buildingAt.longitude,
                // "New building" until it exists, so an existing building's
                // pin nearby never reads as the one being placed.
                label: siteId ? siteName : 'New building',
                kind: 'site' as const,
                draggable: placingBuilding && !drawingBoundary,
              },
            ]
          : []),
        ...(outlineDrawing?.points ?? []),
        ...saved.map((p) => ({
          id: p.id,
          latitude: p.latitude,
          longitude: p.longitude,
          label: p.name,
          kind: 'assembly' as const,
          dim,
        })),
        ...(draft?.at
          ? [
              {
                id: DRAFT_PIN,
                latitude: draft.at.latitude,
                longitude: draft.at.longitude,
                label: draft.name.trim() || (section?.newLabel ?? 'New point'),
                kind: 'assembly' as const,
                draggable: true,
              },
            ]
          : []),
        ...(draftCircle
          ? [
              {
                id: DRAFT_HANDLE,
                // Under the finger while it is being dragged (so the engine
                // never pulls it away mid-drag), due east of the centre at
                // the current radius otherwise.
                ...(handleAt ?? eastOf(draftCircle.at, draftCircle.radiusM)),
                label: 'Drag to resize',
                kind: 'handle' as const,
                draggable: true,
              },
            ]
          : []),
        ...(draftOutline?.points ?? []),
      ],
      circles: [
        ...(buildingAt && boundaryRadius !== undefined && !boundaryRing && !drawingBoundary
          ? [
              {
                id: 'wizard-boundary',
                latitude: buildingAt.latitude,
                longitude: buildingAt.longitude,
                radiusM: boundaryRadius,
                kind: 'boundary' as const,
              },
            ]
          : []),
        ...saved
          .filter((p) => p.radius_m !== null)
          .map((p) => ({
            id: `area-${p.id}`,
            latitude: p.latitude,
            longitude: p.longitude,
            radiusM: p.radius_m!,
            kind: 'assembly' as const,
            dim,
          })),
        ...(draftCircle
          ? [
              {
                id: 'draft-area',
                latitude: draftCircle.at.latitude,
                longitude: draftCircle.at.longitude,
                radiusM: draftCircle.radiusM,
                kind: 'assembly' as const,
              },
            ]
          : []),
      ],
      polygons: [
        // The building's outline being drawn (every corner from the first
        // tap), or the committed one.
        ...(outlineDrawing
          ? outlineDrawing.polygons
          : boundaryRing
            ? [{ id: 'wizard-outline', ring: boundaryRing, kind: 'boundary' as const }]
            : []),
        ...saved
          .filter((p) => p.area_polygon !== null)
          .map((p) => ({
            id: `poly-${p.id}`,
            ring: p.area_polygon!,
            kind: 'assembly' as const,
            dim,
          })),
        ...(draftOutline?.polygons ?? []),
      ],
    };
  }, [
    current,
    siteId,
    siteName,
    buildingAt,
    buildingCommitted,
    assemblyList,
    boundaryDiameter,
    boundaryRing,
    drawingBoundary,
    draft,
    handleAt,
  ]);

  // The camera (item 9): the building alone at building zoom, or the building
  // and every placed assembly point, framed. Recomputed only when the SET of
  // points changes, or on a deliberate re-frame (an address pick, a pin placed
  // by tap) — never on a drag, which would snatch the map from under the
  // finger. Lockdown points sit inside the building and add nothing to it.
  const placedKey = (assemblyList ?? [])
    .filter((p) => p.kind !== 'indoor_shelter')
    .map((p) => p.id)
    .sort()
    .join(',');
  const frameInputs = useRef({
    building: buildingCommitted,
    list: assemblyList,
    draftAt: draft?.at ?? null,
  });
  frameInputs.current = {
    building: buildingCommitted,
    list: assemblyList,
    draftAt: draft?.kind === 'indoor_shelter' ? null : (draft?.at ?? null),
  };
  const hasBuilding = Boolean(buildingCommitted);
  const frame = useMemo(
    () =>
      frameFor(frameInputs.current.building, [
        ...(frameInputs.current.list ?? []).filter((p) => p.kind !== 'indoor_shelter'),
        ...(frameInputs.current.draftAt ? [frameInputs.current.draftAt] : []),
      ]),
    // Keyed deliberately: see above.
    [placedKey, frameTick, hasBuilding],
  );

  // A map tap does what the panel in front of the person is asking for: place
  // the building before it exists, add a corner to an outline being drawn,
  // place (or move) the assembly point being added. Nothing otherwise.
  //
  // Held in a REF read at click time, with one stable dispatcher registered
  // into the substrate — never a per-step closure pushed through substrate
  // state. A closure had a staleness window: a tap landing before the
  // registration round-tripped ran the PREVIOUS step's handler (deterministic
  // on CI, a real race on a slow machine).
  const onMapClick =
    current === 'place' && drawingBoundary
      ? // Outline corners — 64 is the server's cap; taps past it are ignored
        // rather than saved into a 400.
        (latitude: number, longitude: number) =>
          drawingBoundary.length < 64 &&
          setDrawingBoundary([...drawingBoundary, { latitude, longitude }])
      : current === 'place' && !siteId
        ? (latitude: number, longitude: number) => {
            placeBuilding({ latitude, longitude });
            setFrameTick((t) => t + 1);
          }
        : // A created building moves by DRAG only (architecture review H3):
          // a stray click on its confirmation panel must never move it.
          current === 'assembly' && draft
          ? (latitude: number, longitude: number) => {
              const at = { latitude, longitude };
              if (draft.kind !== 'indoor_shelter' && draft.at && draft.area === 'outline') {
                if (draft.ring.length < 64) setDraft({ ...draft, ring: [...draft.ring, at] });
                return;
              }
              moveDraft(at, { readAddress: draft.kind !== 'indoor_shelter' });
            }
          : undefined;
  const mapClickRef = useRef(onMapClick);
  mapClickRef.current = onMapClick;
  // A tap on a saved assembly pin opens it for editing — while nothing else is
  // being edited (an open draft is never thrown away by a stray tap). Other
  // pins (the building, the estate) are not the wizard's to act on. Ref-read
  // at click time, like the click dispatcher.
  const onPinSelect = (pointId: string) => {
    if (current !== 'assembly' || draft) return;
    const p = (assemblyList ?? []).find((pt) => pt.id === pointId);
    if (p) openDraft(p.kind, p);
  };
  const pinSelectRef = useRef(onPinSelect);
  pinSelectRef.current = onPinSelect;

  // A corner of an outline moves where it is dropped.
  const moveCorner = (ring: api.LatLon[], i: number, at: api.LatLon) =>
    ring.map((c, n) => (n === i ? at : c));
  const onPinDrag = (pointId: string, latitude: number, longitude: number) => {
    const at = { latitude, longitude };
    if (pointId === 'wizard-site') {
      // The drop IS the correction: a draft takes the new spot (and the
      // duplicate check sees it); a created building is moved on the server.
      if (siteId) {
        moveBuilding.mutate({ at });
      } else {
        setBuildingDrag(null);
        // A drawn outline travels with its building, as the server does for
        // a created one — so the pin never ends up outside its own outline.
        if (boundaryRing && draftCoord) {
          const dLat = latitude - draftCoord.latitude;
          const dLon = longitude - draftCoord.longitude;
          setBoundaryRing(
            boundaryRing.map((c) => ({
              latitude: c.latitude + dLat,
              longitude: c.longitude + dLon,
            })),
          );
        }
        placeBuilding(at);
      }
      return;
    }
    const buildingCorner = cornerIndex(pointId, BUILDING_OUTLINE);
    if (buildingCorner !== null && drawingBoundary) {
      setDrawingBoundary(moveCorner(drawingBoundary, buildingCorner, at));
      return;
    }
    if (!draft) return;
    if (pointId === DRAFT_PIN) {
      moveDraft(at, { readAddress: draft.kind !== 'indoor_shelter' });
      return;
    }
    if (pointId === DRAFT_HANDLE && draft.at) {
      setHandleAt(null);
      setDraft((d) => (d?.at ? { ...d, radiusM: clampRadius(metresBetween(d.at, at)) } : d));
      return;
    }
    const corner = cornerIndex(pointId, DRAFT_OUTLINE);
    if (corner !== null) setDraft((d) => (d ? { ...d, ring: moveCorner(d.ring, corner, at) } : d));
  };
  const onPinDragging = (pointId: string, latitude: number, longitude: number) => {
    const at = { latitude, longitude };
    if (pointId === 'wizard-site') {
      setBuildingDrag(at);
      return;
    }
    const buildingCorner = cornerIndex(pointId, BUILDING_OUTLINE);
    if (buildingCorner !== null && drawingBoundary) {
      setDrawingBoundary(moveCorner(drawingBoundary, buildingCorner, at));
      return;
    }
    if (!draft) return;
    if (pointId === DRAFT_PIN) {
      moveDraft(at, { readAddress: false });
    } else if (pointId === DRAFT_HANDLE && draft.at) {
      setHandleAt(at);
      setDraft((d) => (d?.at ? { ...d, radiusM: clampRadius(metresBetween(d.at, at)) } : d));
    } else {
      const corner = cornerIndex(pointId, DRAFT_OUTLINE);
      if (corner !== null)
        setDraft((d) => (d ? { ...d, ring: moveCorner(d.ring, corner, at) } : d));
    }
  };
  const pinDraggingRef = useRef(onPinDragging);
  pinDraggingRef.current = onPinDragging;
  const pinDragRef = useRef(onPinDrag);
  pinDragRef.current = onPinDrag;

  const draftName = draft?.name.trim() || 'the point';
  const mapHint =
    current === 'place' && drawingBoundary
      ? `Tap the map at each corner of the building — ${drawingBoundary.length} so far. Drag a corner to adjust it. Finish or cancel in the panel.`
      : current === 'place' && buildingAt
        ? // Once there is a pin, the hint stops inviting another one (Trav,
          // 2026-09-30: "feels like adding two buildings").
          boundaryRing
          ? 'Drag the pin to adjust — the outline is the building’s boundary.'
          : 'Drag the pin to adjust — the circle is the building’s boundary.'
        : current === 'place'
          ? 'Type an address, or tap the map to place the building. Set “How far across” to draw its boundary.'
          : current === 'assembly'
            ? !draft
              ? 'Add a point in the panel — or tap a point on the map to change it.'
              : draft.kind === 'indoor_shelter'
                ? draft.at
                  ? `Drag the pin to where ${draftName} is inside the building.`
                  : `Tap the map where ${draftName} is.`
                : !draft.at
                  ? `Type the address in the panel, or tap the map where ${draftName} is.`
                  : draft.area === 'outline'
                    ? `Tap the map at each corner of the area — ${draft.ring.length} so far. Drag a corner to adjust it.`
                    : draft.area === 'circle'
                      ? 'Drag the pin to adjust, and the ↔ handle to size the area.'
                      : 'Drag the pin to adjust, or tap the map to move it.'
            : null;

  // Standalone, the wizard's map IS the persistent substrate (owner direction
  // 2026-09-21: panel out from the rail over the left half, the app's own map
  // living on the right) — its content and tap handler are registered here and
  // cleared by the hook on unmount. Embedded in the setup flow the wizard sits
  // inside an ordinary sheet where the substrate is deliberately ambient, so it
  // keeps its own side map and registers nothing.
  const hideSiteIds = useMemo(() => {
    const ids: string[] = [];
    if (siteId && current === 'place') ids.push(siteId);
    // A lockdown point's pin starts ON the building: hide the building's pin
    // while they sit together, so the one being dragged is never underneath.
    if (
      siteId &&
      current === 'assembly' &&
      draft?.kind === 'indoor_shelter' &&
      draft.at &&
      buildingCommitted &&
      metresBetween(draft.at, buildingCommitted) <= NEAR_DRAFT_M
    )
      ids.push(siteId);
    if (buildingAt) {
      for (const s of orgSites.data?.sites ?? []) {
        if (s.id === siteId || s.latitude == null || s.longitude == null) continue;
        if (
          metresBetween(buildingAt, { latitude: s.latitude, longitude: s.longitude }) <=
          NEAR_DRAFT_M
        )
          ids.push(s.id);
      }
    }
    return ids;
  }, [siteId, current, draft, buildingAt, buildingCommitted, orgSites.data]);
  const canvas = useMapCanvas();
  useEffect(() => {
    if (embedded) return;
    // The panel covers the map's left edge up to max(760px, 50vw) on desktop
    // (rail 240 + the panel's 520 floor / half-screen width) — tell the map so
    // the camera centres in the VISIBLE half, not under the panel edge. Below
    // lg the panel is full-width and the map is behind it entirely.
    const insetLeftPx =
      window.innerWidth >= 1024 ? Math.max(760, Math.round(window.innerWidth / 2)) : 0;
    canvas.setContent({
      points: stage.points,
      circles: stage.circles,
      polygons: stage.polygons,
      insetLeftPx,
      // Centre on what is being placed, at building level (~400 m across —
      // "zoom right in", Trav 2026-09-21), widening to take in every placed
      // assembly point (item 9); before anything is placed, leave the
      // substrate's own estate/building focus in charge.
      ...(frame ? { focus: frame } : {}),
      // One pin per building: the estate's copy of the building being placed
      // (the wizard draws its own, draggable) and any existing building sitting
      // right on the draft — the duplicate warning names that one in words.
      hideSiteIds,
      // Once there is a building to look at, show the real roof: satellite
      // imagery (labels kept) while placing it and its assembly points, where
      // "is that my building / is that the car park" is the whole question.
      // Apple's web map has no 3D or building highlight (researched
      // 2026-09-29), so the imagery is the honest way to show the building.
      satellite: Boolean(stage.focusAt) && (current === 'place' || current === 'assembly'),
    });
    // The setters are useCallback-stable (map-layer.tsx).
  }, [embedded, canvas.setContent, stage, current, frame, hideSiteIds]);
  useEffect(() => {
    if (embedded) return;
    canvas.setInteraction({
      onMapClick: (latitude, longitude) => mapClickRef.current?.(latitude, longitude),
      onSelect: (pointId) => pinSelectRef.current(pointId),
      // Press-and-drag moves a pin on either engine (`lib/marker-drag.ts`);
      // which pins may move is per-point in the stage.
      onDragEnd: (pointId, latitude, longitude) => pinDragRef.current(pointId, latitude, longitude),
      onDrag: (pointId, latitude, longitude) =>
        pinDraggingRef.current(pointId, latitude, longitude),
    });
  }, [embedded, canvas.setInteraction]);
  // One form per step at the rail's size (owner, 2026-10-14) — the raise
  // panel's register. Standalone the column is bounded like the raise panel's;
  // embedded, setup's own sheet bounds it.
  const inner = (
    <div className={embedded ? '' : 'mx-auto max-w-[520px]'}>
      {embedded ? null : <Stepper current={current} unlocked={Boolean(siteId)} onGo={goTo} />}

      <div className={embedded ? '' : 'mt-4'}>
        {current === 'place' ? (
          <PlaceStep
            orgId={orgId}
            embedded={embedded}
            created={Boolean(siteId)}
            createdName={siteName}
            createdId={siteId}
            createdAddress={siteAddress}
            onAddressSaved={setSiteAddress}
            presenceDefault={presenceDefault}
            picked={draftCoord}
            onPick={(c) => {
              setDraftCoord(c);
              setFrameTick((t) => t + 1);
            }}
            addressRegion={addressRegion}
            pinAddress={pinAddress}
            boundaryDiameter={boundaryDiameter}
            onBoundaryDiameter={setBoundaryDiameter}
            outline={{
              ring: boundaryRing,
              drawing: drawingBoundary,
              canStart: Boolean(buildingAt),
              onStart: () => setDrawingBoundary([]),
              onFinish: () => {
                if (drawingBoundary && drawingBoundary.length >= 3) {
                  setBoundaryRing(drawingBoundary);
                  setDrawingBoundary(null);
                }
              },
              onCancel: () => setDrawingBoundary(null),
              onUseCircle: () => {
                setBoundaryRing(null);
                setDrawingBoundary(null);
              },
            }}
            moved={{
              duplicate: moveDuplicate?.site ?? null,
              error: moveError,
              pending: moveBuilding.isPending,
              onConfirmDistinct: () =>
                moveDuplicate &&
                moveBuilding.mutate({ at: moveDuplicate.at, confirmDistinct: true }),
            }}
            onCreated={(id, name, c, address) => {
              setSiteAddress(address);
              setSiteId(id);
              setSiteName(name);
              setSiteCoord(c);
              next();
            }}
            onContinue={next}
            onExit={embedded ? undefined : onExit}
          />
        ) : null}

        {current === 'assembly' && siteId ? (
          <AssemblyStep
            embedded={embedded}
            list={assemblyList}
            loading={points.isLoading}
            draft={draft}
            onDraft={(change) => setDraft((d) => (d ? { ...d, ...change } : d))}
            onStart={(kind) => openDraft(kind)}
            onEdit={(p) => openDraft(p.kind, p)}
            onPlaceByAddress={(at) => {
              moveDraft(at, { readAddress: false });
              setFrameTick((t) => t + 1);
            }}
            addressRegion={assemblyRegion}
            onSave={() => draft && savePoint.mutate(draft)}
            saving={savePoint.isPending}
            onCancel={() => {
              setDraft(null);
              setHandleAt(null);
              setPointError(null);
            }}
            onDelete={(id) => deletePoint.mutate(id)}
            deleting={deletePoint.isPending ? (deletePoint.variables ?? null) : null}
            error={pointError}
            buildingPlaced={Boolean(buildingCommitted)}
            onNext={next}
            onBack={back}
            onFinish={finish}
            skipLabel={skipLabel}
          />
        ) : null}
        {current === 'zones' && siteId ? (
          <ZonesStep
            embedded={embedded}
            orgId={orgId}
            siteId={siteId}
            onNext={next}
            onBack={back}
            onFinish={finish}
            skipLabel={skipLabel}
          />
        ) : null}

        {current === 'wardens' && siteId ? (
          <WardensStep
            embedded={embedded}
            orgId={orgId}
            siteId={siteId}
            onNext={next}
            onBack={back}
            onFinish={finish}
            skipLabel={skipLabel}
          />
        ) : null}

        {current === 'people' && siteId ? (
          <AlertedStep
            embedded={embedded}
            orgId={orgId}
            siteId={siteId}
            onBack={back}
            onFinish={finish}
            finishLabel={finishLabel}
          />
        ) : null}
      </div>
    </div>
  );

  // Embedded in the setup flow: the wizard lives inside setup's own sheet,
  // where the substrate map is deliberately ambient — so it keeps a side map of
  // its own, two columns on a wide screen.
  if (embedded) {
    return (
      <div className="lg:grid lg:grid-cols-[minmax(0,680px)_minmax(320px,1fr)] lg:items-start lg:gap-8">
        <div>{inner}</div>
        <aside className="mt-8 lg:sticky lg:top-[calc(var(--dir-header-h,0px)+20px)] lg:mt-0">
          <Map
            points={stage.points}
            circles={stage.circles}
            polygons={stage.polygons}
            height="clamp(280px, calc(100vh - 240px), 640px)"
            onClick={onMapClick}
            onSelect={onPinSelect}
            onDragEnd={onPinDrag}
            onDrag={onPinDragging}
            fallbackCenter={defaultCenter()}
          />
          {mapHint ? <p className="mt-2 text-[13px] text-[var(--ink-2)]">{mapHint}</p> : null}
        </aside>
      </div>
    );
  }

  // Standalone (owner direction 2026-09-21): a glass panel sliding out from the
  // rail over the LEFT half of the screen, with the app's own persistent map —
  // fed by the registration effects above — living on the right. The Shell
  // renders this route `bleed` (main is pointer-events-none, like Home), so the
  // panel opts back in and every map gesture on the right falls through to the
  // substrate. The hint floats over the map as a glass chip, pointer-inert so
  // it never eats a tap meant for the coordinate under it.
  return (
    <>
      {/* Anchored ABSOLUTELY inside <main> (which is relative and starts
          exactly under the top bar + banner), not fixed with a guessed top —
          the guess left a strip of bare map above the panel whenever the
          banner's height differed (Trav 2026-09-21). `lg:ml` is not needed:
          main already carries the rail offset. */}
      <div className="panel-in-left pointer-events-auto absolute inset-y-0 left-0 z-20 w-full lg:w-[max(520px,calc(50vw-240px))]">
        <div className="sheet h-full overflow-y-auto border-t border-r border-[var(--glass-edge)] p-[var(--sheet-pad)]">
          {inner}
        </div>
      </div>
      {/* xl, not lg: at narrow desktop widths the panel's 520px floor crosses
          the halfway line and the hint would sit over the step nav. */}
      {mapHint ? (
        <div className="pointer-events-none fixed right-6 bottom-6 z-20 hidden w-[min(340px,calc(50vw-3rem))] xl:block">
          <p className="sheet elev-1 rounded-[var(--radius-2)] border border-[var(--glass-edge)] px-4 py-3 text-[13px] text-[var(--ink-2)]">
            {mapHint}
          </p>
        </div>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- the stepper */

/**
 * The stepper, matching the setup wizard's (setup.tsx) so the two read as one
 * family. Completion is carried by colour on an `aria-hidden` dot, so it is said
 * in words too: `aria-current` marks the current step and an `sr-only` status
 * word carries the rest. Steps after "Place it" are disabled until the building
 * exists — they have nothing to attach to before then.
 */
function Stepper({
  current,
  unlocked,
  onGo,
}: {
  current: WizardStep;
  unlocked: boolean;
  onGo: (id: WizardStep) => void;
}) {
  const currentIdx = STEPS.findIndex((s) => s.id === current);
  // One line, always (owner correction 2026-09-25: the bordered chips wrapped
  // to two rows and read as five competing buttons). The chrome is gone — a
  // numbered dot and a word per step, thin separators, the current step in
  // full ink — so it reads as a position in a sequence, not as a menu.
  return (
    <nav
      aria-label="Building setup steps"
      className="flex items-center gap-0.5 overflow-x-auto whitespace-nowrap"
    >
      {STEPS.map((s, i) => {
        const active = s.id === current;
        // A step reads as done once it is behind the current one and the
        // building it all hangs off exists — the first step is the only one with
        // a hard "created" signal, the rest are soft, so nothing claims a "done"
        // it cannot prove.
        const done = unlocked && i < currentIdx;
        // Later steps are dead until the building is created, and "Place it" is
        // never a backward jump worth blocking.
        const locked = !unlocked && s.id !== 'place';
        const status = locked
          ? 'not available yet'
          : done
            ? 'completed'
            : active
              ? 'current'
              : 'not started';
        return (
          <span key={s.id} className="flex shrink-0 items-center">
            {i > 0 ? (
              <span aria-hidden className="mx-1 text-[12px] text-[var(--ink-3)]">
                ›
              </span>
            ) : null}
            <button
              type="button"
              disabled={locked}
              onClick={() => onGo(s.id)}
              aria-current={active ? 'step' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-[var(--radius-1)] px-1 py-1 text-[12px] leading-[16px] ${
                active
                  ? 'font-medium text-[var(--ink)]'
                  : locked
                    ? 'text-[var(--ink-3)]'
                    : 'text-[var(--ink-2)] hover:text-[var(--ink)]'
              }`}
            >
              <span
                aria-hidden
                className={`inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full text-[10px] ${
                  done
                    ? 'bg-[var(--safe)] text-[var(--paper)]'
                    : active
                      ? 'bg-[var(--ink)] text-[var(--paper)]'
                      : 'border border-[var(--line-strong)] text-[var(--ink-3)]'
                }`}
              >
                {done ? '✓' : i + 1}
              </span>
              {s.label}
              <span className="sr-only"> — {status}</span>
            </button>
          </span>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------- the step shape */

/**
 * Each step is one form, the raise panel's shape (owner, 2026-10-14: "a much
 * smaller space, following Apple best practice", at "the font size of the left
 * hand menu"): a 20px heading, a column of fields at one rhythm, a line of help
 * only where it says something the label does not, the step's one action
 * full-width at the foot and the quiet ways out under it. Standalone the
 * heading is the page's h1; embedded in the setup flow (which owns the h1) it
 * is an h2, so heading order holds either way.
 */
function StepHeading({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  const cls = 'm-0 text-[20px] leading-[24px] font-[650] tracking-[-0.01em] text-[var(--ink)]';
  return embedded ? <h2 className={cls}>{children}</h2> : <h1 className={cls}>{children}</h1>;
}

/**
 * The foot of every step: the primary, 64px and full-width (the one thing the
 * step does), and the quiet ways out centred beneath it — Back, Skip, or the
 * pre-creation Cancel. A disabled primary greys and stays put, so the layout
 * cannot shift under a moving pointer (`docs/design-dashboard.md`).
 */
function StepActions({
  primary,
  primaryDisabled,
  onPrimary,
  onBack,
  onSkip,
  skipLabel,
  onCancel,
}: {
  primary: ReactNode;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  onBack?: () => void;
  /** Jump straight to the end — the building detail, or setup's next step. */
  onSkip?: () => void;
  skipLabel?: string;
  onCancel?: () => void;
}) {
  const quiet = onBack || onSkip || onCancel;
  return (
    <div className="mt-7">
      <Button variant="primary" size="live" full disabled={primaryDisabled} onClick={onPrimary}>
        {primary}
      </Button>
      {quiet ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-6">
          {onBack ? (
            <Button variant="quiet" onClick={onBack}>
              Back
            </Button>
          ) : null}
          {onSkip ? (
            <Button variant="quiet" onClick={onSkip}>
              {skipLabel}
            </Button>
          ) : null}
          {onCancel ? (
            <Button variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A 44px field with a short action beside it — "Add an area", "Invite",
 * "Create and alert". The field takes the width; the button keeps the table
 * row's 36px and sits centred on the field.
 */
function FieldWithAction({ field, action }: { field: ReactNode; action: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">{field}</div>
      <span className="shrink-0">{action}</span>
    </div>
  );
}

/* --------------------------------------------------------------------- step 1 */

/**
 * Place it — name, address (which places the pin), phone and the sensitive flag.
 * Completing this **creates the building** (reusing `createSite` for the extras
 * it already supports — coords and sensitive — and `updateSite` for the phone),
 * so the following steps have a real `siteId` to attach to. A building with
 * nothing else yet is a legitimate "being set up" state; readiness shows the
 * gaps.
 */
function PlaceStep({
  orgId,
  embedded,
  created,
  createdName,
  createdId,
  createdAddress,
  onAddressSaved,
  presenceDefault,
  picked,
  onPick,
  addressRegion,
  pinAddress,
  boundaryDiameter,
  onBoundaryDiameter,
  outline,
  moved,
  onCreated,
  onContinue,
  onExit,
}: {
  orgId: string;
  embedded: boolean;
  created: boolean;
  createdName: string;
  /** After creation: the building's id and its address on file. */
  createdId?: string;
  createdAddress?: string;
  onAddressSaved?: (address: string) => void;
  presenceDefault?: boolean;
  /**
   * The building's coordinate — lifted to the wizard (piece 2b) so the
   * right-hand map draws the pin live, and a tap ON the map sets it just like
   * picking an address suggestion does.
   */
  picked: { latitude: number; longitude: number } | null;
  onPick: (c: { latitude: number; longitude: number } | null) => void;
  /** Where address search looks first — the org's buildings, else its country. */
  addressRegion?: SearchRegion | null;
  /** What the pin reads as after a tap or a drop — written into the field. */
  pinAddress?: { text: string; n: number } | null;
  /** "How far across" — drawn live as the boundary circle, halved on create. */
  boundaryDiameter: string;
  onBoundaryDiameter: (v: string) => void;
  /** The drawn outline instead of the circle (migration 0140). */
  outline?: {
    ring: api.LatLon[] | null;
    drawing: api.LatLon[] | null;
    canStart: boolean;
    onStart: () => void;
    onFinish: () => void;
    onCancel: () => void;
    onUseCircle: () => void;
  };
  /** After creation: a pin moved onto another building, or a refused move. */
  moved?: {
    duplicate: DuplicateSite | null;
    error: string | null;
    pending: boolean;
    onConfirmDistinct: () => void;
  };
  onCreated: (
    siteId: string,
    name: string,
    coord: { latitude: number; longitude: number } | null,
    address: string,
  ) => void;
  onContinue: () => void;
  onExit?: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  // A tap or a drop reads the pin back as an address, and the field follows
  // the pin (Trav, 2026-10-01: "when I drag it somewhere else, the address
  // should update in the address input field"). Moving the pin is the person
  // saying where the building is, so its reading replaces what was there —
  // before creation in the create form, after it in the field below the pin
  // (saved only when they say so). Unit and level are separate fields and are
  // never touched.
  const [placedAddress, setPlacedAddress] = useState(createdAddress ?? '');
  useEffect(() => {
    setPlacedAddress(createdAddress ?? '');
  }, [createdAddress]);
  useEffect(() => {
    if (!pinAddress) return;
    if (created) setPlacedAddress(pinAddress.text);
    else setAddress(pinAddress.text);
  }, [pinAddress]);
  const [addressError, setAddressError] = useState<string | null>(null);
  const saveAddress = useMutation({
    mutationFn: (next: string) => api.updateSite(orgId, createdId ?? '', { address: next.trim() }),
    onSuccess: (_r, next) => {
      setAddressError(null);
      onAddressSaved?.(next.trim());
      void qc.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (e: Error) => setAddressError(sayRefusal(e, 'save the address')),
  });
  // The sub-premise (migration 0110): autocomplete returns the street line, so
  // "Suite 1, Level 8" is captured in its own fields beside it.
  const [unit, setUnit] = useState('');
  const [level, setLevel] = useState('');
  // The number in two halves (owner correction 2026-09-25): a country code
  // select and the national number. Composed on save; a pasted full "+…"
  // number wins over the select so nothing double-prefixes.
  const [dial, setDial] = useState('+61');
  const [phone, setPhone] = useState('');
  const [sensitive, setSensitive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One building, one site: warn the moment the address or pin is already one
  // of this org's buildings, and read the server's 409 if a race beats it.
  const existing = useSiteMatch(orgId, { address, unit, level, coord: picked });
  const [raced, setRaced] = useState<DuplicateSite | null>(null);
  useEffect(() => {
    setRaced(null);
  }, [address, unit, level, picked]);
  const duplicate = existing ?? raced;

  const composedPhone = () => {
    const p = phone.trim();
    if (p === '') return '';
    if (p.startsWith('+') || dial === '') return p;
    // The national trunk zero goes when a country code fronts the number —
    // "+61 3 9000 0000", never "+61 03 9000 0000".
    return `${dial} ${p.replace(/^0/, '')}`;
  };

  const create = useMutation({
    // The mutation does ONE thing that can fail fatally: create the building.
    // Phone is attached as a NON-fatal follow-up in onSuccess, not awaited here
    // — board HIGH: with both calls inside one mutationFn, a network blip between
    // createSite and updateSite rejected the whole mutation, discarded the
    // already-created siteId, and a retry ran createSite a second time, forking
    // the building table (a first orphan with no phone, then a duplicate). The
    // created building is what matters; phone is editable on its page, so its
    // write is best-effort and a retry now attaches to the existing building.
    mutationFn: async (opts?: { confirmDistinct?: boolean }) => {
      // Who can raise a real alert here is asked on the wardens step, not here
      // (product owner, 2026-09-30): the server stores one administrator,
      // unchosen, and readiness asks until somebody chooses.
      const extras: api.CreateSiteExtras = { sensitive };
      if (opts?.confirmDistinct) extras.confirmDistinct = true;
      if (unit.trim()) extras.unit = unit.trim();
      if (level.trim()) extras.level = level.trim();
      if (picked) {
        extras.latitude = picked.latitude;
        extras.longitude = picked.longitude;
      }
      // The boundary the map is already drawing: stored as a radius (#472).
      // Only meaningful with a coordinate to centre it on.
      const boundaryRadius = diameterToRadius(boundaryDiameter);
      // One shape: a drawn outline (0140) wins over the circle.
      if (outline?.ring && outline.ring.length >= 3) extras.boundaryPolygon = outline.ring;
      else if (picked && boundaryRadius !== undefined) extras.geofenceRadiusM = boundaryRadius;
      // Carry the shape's presence default (counted vs roster-based) into
      // creation so a café stays roster-based and a building counts presence —
      // migration-0081 capability that is not editable after creation, so it
      // cannot be dropped at the create step. Undefined (standalone add) takes
      // the server default.
      if (presenceDefault !== undefined) extras.presenceEnabled = presenceDefault;
      const result = await api.createSite(orgId, name.trim(), address.trim() || undefined, extras);
      // `api<T>` resolves to `T | null`; a create that returns no id is a server
      // error rather than a success, so surface it rather than advancing with an
      // empty siteId the later steps could not attach to.
      if (!result?.siteId) throw new Error('The building could not be created. Try again.');
      return { siteId: result.siteId };
    },
    onSuccess: ({ siteId }) => {
      setError(null);
      // Seed the sites list with the new building BEFORE navigating — board
      // HIGH: onComplete navigates to /locations/<siteId>, and an invalidate
      // alone only marks the list stale and fires an async refetch. Because the
      // list already had data, `sites.isPending` stays false, so a fast operator
      // who skips straight out lands on "that building was not found" over the
      // building they just made. Seeding makes the row present the instant the
      // page reads it; the invalidate then refreshes it to the server's own row.
      qc.setQueryData<{ sites: api.Site[] } | undefined>(['sites', orgId], (prev) => {
        const row: api.Site = {
          id: siteId,
          name: name.trim(),
          slug: '',
          address: address.trim() || null,
          unit: unit.trim() || null,
          level: level.trim() || null,
          phone: composedPhone() || null,
          latitude: picked?.latitude ?? null,
          longitude: picked?.longitude ?? null,
          geofence_radius_m:
            picked && !outline?.ring ? (diameterToRadius(boundaryDiameter) ?? null) : null,
          boundary_polygon: outline?.ring ?? null,
          sensitive,
          activation_rule: 'one_person',
          activation_rule_chosen_at: null,
          followers: 0,
        };
        if (!prev) return { sites: [row] };
        if (prev.sites.some((s) => s.id === siteId)) return prev;
        return { sites: [...prev.sites, row] };
      });
      // Phone: best-effort, off the critical path. A failure here never orphans
      // or double-creates; it is editable on the building page. Re-invalidate
      // once it lands so the summary reflects it.
      if (composedPhone() !== '') {
        void api
          .updateSite(orgId, siteId, { phone: composedPhone() })
          .then(() => qc.invalidateQueries({ queryKey: ['sites'] }))
          .catch(() => undefined);
      }
      void qc.invalidateQueries({ queryKey: ['sites'] });
      onCreated(siteId, name.trim(), picked, address.trim());
    },
    onError: (e: Error) => {
      const dup = duplicateFromError(e);
      setRaced(dup);
      setError(dup ? null : sayRefusal(e, 'create this building'));
    },
  });

  // "It's a different building" (#567) — it did nothing (Trav, 2026-09-30):
  // the button was greyed by a prerequisite it never named (the activation
  // choice below it, or a missing name). It now always acts: it creates with
  // the audited override, or it says what is missing.
  const confirmDistinct = () => {
    if (!name.trim()) {
      setError('Name the building first — then it can be created beside the existing one.');
      return;
    }
    setError(null);
    create.mutate({ confirmDistinct: true });
  };

  // The building is already created and the operator stepped back to this
  // screen: this step is done, so it says so and offers only the way forward.
  // Editing a created building's details is the detail page's job, not a
  // re-creation here (there is no re-create).
  if (created) {
    return (
      <div>
        <StepHeading embedded={embedded}>Place it</StepHeading>
        <div className="mt-5 space-y-3">
          <Finding tone="safe">
            <strong>{createdName || 'The building'}</strong> is created. Drag its pin on the map if
            it isn&rsquo;t in the middle of the building.
          </Finding>
          <div>
            <FieldWithAction
              field={
                <FieldInput label="Address" value={placedAddress} onChange={setPlacedAddress} />
              }
              action={
                <Button
                  disabled={
                    saveAddress.isPending || placedAddress.trim() === (createdAddress ?? '').trim()
                  }
                  onClick={() => saveAddress.mutate(placedAddress)}
                >
                  {saveAddress.isPending ? 'Saving…' : 'Save address'}
                </Button>
              }
            />
            <Help>Follows the pin when you drag it — saved only when you save it.</Help>
          </div>
          {addressError ? <Finding>{addressError}</Finding> : null}
          {moved?.duplicate ? (
            <DuplicateSiteFinding
              site={moved.duplicate}
              confirming={moved.pending}
              onConfirmDistinct={moved.onConfirmDistinct}
            />
          ) : null}
          {moved?.error ? <Finding>{moved.error}</Finding> : null}
        </div>
        <StepActions primary="Continue" onPrimary={onContinue} />
      </div>
    );
  }

  return (
    <div>
      <StepHeading embedded={embedded}>Place it</StepHeading>

      {/* One form, the Apple Business shape: the name, the address, the
          sub-premise, the phone, the boundary and the privacy switch — fields
          at one rhythm, nothing explained under a control the label already
          says. Only the name is required; the one help line says so. */}
      <div className="mt-5 space-y-3">
        <FieldInput
          label="Building name"
          value={name}
          onChange={setName}
          placeholder="Lincoln Primary"
          autoFocus
        />
        {/* MapKit JS autocomplete — the same engine the dashboard's maps use,
            so no new vendor and no CSP change. A picked suggestion hands back a
            coordinate, so the building is on the map from the moment it exists;
            falls back to a plain field when MapKit is unavailable. No inline
            preview: the building shows on the wizard's map, and a tap on it
            places the pin too. */}
        <AddressAutocomplete
          appearance="field"
          label="Address"
          value={address}
          onChange={setAddress}
          onPick={(c) => onPick(c)}
          region={addressRegion ?? null}
          offerMyLocation
          eager
          preview={false}
        />
        {/* The sub-premise (migration 0110) beside the number: autocomplete
            gives "420 St Kilda Rd", not "Suite 1, Level 8". */}
        <div className="grid grid-cols-2 gap-3">
          <FieldInput label="Unit or suite" value={unit} onChange={setUnit} placeholder="Suite 1" />
          <FieldInput label="Level" value={level} onChange={setLevel} placeholder="8" />
        </div>
        <div>
          <PhoneField dial={dial} onDial={setDial} phone={phone} onPhone={setPhone} />
          <Help>The building&rsquo;s own number. Only the name is required here.</Help>
        </div>

        {/* The boundary, asked as a DIAMETER because that is how somebody
            eyeballs a building ("about 80 metres across"); stored halved as
            geofence_radius_m (#472) and drawn live on the map as it is set. */}
        {outline?.ring || outline?.drawing ? null : (
          <div>
            <AreaAcross
              label="How far across (m)"
              value={boundaryDiameter}
              onChange={onBoundaryDiameter}
              max={500}
            />
            <Help>The building&rsquo;s boundary, drawn on the map as you set it.</Help>
          </div>
        )}
        {outline ? <OutlineControls {...outline} /> : null}

        <FieldGroup label="Privacy">
          <li>
            <Switch
              checked={sensitive}
              onChange={() => setSensitive((v) => !v)}
              ariaLabel="Sensitive site"
              describedBy="sensitive-site-why"
            >
              <span className="block text-[14px] leading-[18px]">Sensitive site</span>
              <span
                id="sensitive-site-why"
                className="block text-[12px] leading-[15px] text-[var(--ink-2)]"
              >
                Never listed publicly — a court, refuge or custodial building. Can only be set now.
              </span>
            </Switch>
          </li>
        </FieldGroup>

        {duplicate ? (
          <DuplicateSiteFinding
            site={duplicate}
            confirming={create.isPending}
            onConfirmDistinct={confirmDistinct}
          />
        ) : null}

        {error ? <Finding>{error}</Finding> : null}
      </div>

      <StepActions
        primary={create.isPending ? 'Creating…' : 'Create building'}
        primaryDisabled={!name.trim() || create.isPending || duplicate !== null}
        onPrimary={() => create.mutate({})}
        onCancel={onExit}
      />
      {duplicate ? (
        <Help>
          This building is already set up — open it, or confirm it&rsquo;s a different building.
        </Help>
      ) : null}
    </div>
  );
}

/**
 * The phone in the field's shape: the country code and the national number are
 * ONE control under one label (owner correction 2026-09-25) — the code select is
 * not a field of its own, it is half of the number.
 */
function PhoneField({
  dial,
  onDial,
  phone,
  onPhone,
}: {
  dial: string;
  onDial: (v: string) => void;
  phone: string;
  onPhone: (v: string) => void;
}) {
  return (
    <div className="relative h-11 w-full rounded-[10px] border border-[var(--line-2)] bg-[var(--paper)]/80 has-focus-visible:border-[var(--focus)] has-focus-visible:ring-1 has-focus-visible:ring-[var(--focus)]">
      <FieldLabel>Phone</FieldLabel>
      <div className="absolute inset-x-3 top-[21px] bottom-0 flex items-center gap-2">
        <span className="relative shrink-0">
          <select
            aria-label="Country code"
            value={dial}
            onChange={(e) => onDial(e.target.value)}
            className="h-[20px] appearance-none bg-transparent pr-4 text-[14px] leading-[18px] text-[var(--ink)] outline-none"
          >
            <option value="+61">AU +61</option>
            <option value="+64">NZ +64</option>
            <option value="+44">UK +44</option>
            <option value="+1">US +1</option>
            <option value="">Other</option>
          </select>
          <svg
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-0 -translate-y-1/2 text-[var(--ink-2)]"
            width="10"
            height="10"
            viewBox="0 0 14 14"
            fill="none"
          >
            <path
              d="M3 5l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span aria-hidden className="h-[14px] w-px shrink-0 bg-[var(--line-2)]" />
        <input
          value={phone}
          onChange={(e) => onPhone(e.target.value)}
          inputMode="tel"
          placeholder="Front desk or security"
          aria-label="Phone number"
          className="h-[20px] min-w-0 flex-1 bg-transparent text-[14px] leading-[18px] text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]"
        />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- step 2 */

/**
 * Assembly points — where people are sent, of the three kinds migration 0030
 * names. One point is added or edited at a time, in the order a person thinks
 * about it (Trav, 2026-10-01): name it; place it, by address or by a tap on the
 * map (the pin then drags, and the address follows it); give it an area — a
 * circle around the point or a drawn outline; save. Saved points are compact
 * rows in the kind's group, with Edit and Delete, and "Add another" is the
 * group's last row. A lockdown point is a place inside the building, so it is a
 * name and a pin dragged to the spot — no address, no area. Skippable: a
 * building can be set up with none, and readiness surfaces the gap.
 */
function AssemblyStep({
  embedded,
  list,
  loading,
  draft,
  onDraft,
  onStart,
  onEdit,
  onPlaceByAddress,
  addressRegion,
  onSave,
  saving,
  onCancel,
  onDelete,
  deleting,
  error,
  buildingPlaced,
  onNext,
  onBack,
  onFinish,
  skipLabel,
}: {
  embedded: boolean;
  list: api.AssemblyPoint[] | undefined;
  loading: boolean;
  /** The one point being added or edited, or null. */
  draft: PointDraft | null;
  onDraft: (change: Partial<PointDraft>) => void;
  onStart: (kind: api.AssemblyKind) => void;
  onEdit: (point: api.AssemblyPoint) => void;
  /** A suggestion was picked — the pin goes there and the camera follows. */
  onPlaceByAddress: (at: api.LatLon) => void;
  /** Where address search looks first — the building just placed. */
  addressRegion: SearchRegion | null;
  onSave: () => void;
  saving: boolean;
  onCancel: () => void;
  onDelete: (id: string) => void;
  /** The id whose delete is in flight. */
  deleting: string | null;
  error: string | null;
  buildingPlaced: boolean;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  skipLabel: string;
}) {
  const editor = (sec: (typeof ASSEMBLY_SECTIONS)[number]) =>
    draft ? (
      <PointEditor
        draft={draft}
        section={sec}
        onDraft={onDraft}
        onPlaceByAddress={onPlaceByAddress}
        addressRegion={addressRegion}
        onSave={onSave}
        saving={saving}
        onCancel={onCancel}
        buildingPlaced={buildingPlaced}
      />
    ) : null;

  return (
    <div>
      <StepHeading embedded={embedded}>Where do people go?</StepHeading>

      <div className="mt-5 space-y-3">
        {loading ? (
          <Spinner />
        ) : (
          // The three kinds as three groups, each with the one line that says
          // what it IS (owner direction 2026-09-21): choosing the wrong kind
          // sends people — or their parents — to the wrong place.
          ASSEMBLY_SECTIONS.map((sec) => {
            const mine = (list ?? []).filter((p) => p.kind === sec.kind);
            const addingHere = draft !== null && draft.id === null && draft.kind === sec.kind;
            return (
              <div key={sec.kind}>
                <FieldGroup label={sec.title} listLabel={sec.title}>
                  {mine.map((p) =>
                    draft?.id === p.id ? (
                      <li key={p.id}>{editor(sec)}</li>
                    ) : (
                      <SavedPointRow
                        key={p.id}
                        point={p}
                        editingOther={draft !== null}
                        onEdit={() => onEdit(p)}
                        onDelete={() => onDelete(p.id)}
                        deleting={deleting === p.id}
                      />
                    ),
                  )}
                  <li>
                    {addingHere ? (
                      editor(sec)
                    ) : (
                      // One point at a time: while another is open this greys
                      // and stays put rather than vanishing.
                      <AddRow disabled={draft !== null} onClick={() => onStart(sec.kind)}>
                        {mine.length > 0 ? sec.addAnotherLabel : sec.addLabel}
                      </AddRow>
                    )}
                  </li>
                </FieldGroup>
                <Help>{sec.explain}</Help>
              </div>
            );
          })
        )}

        {error ? <Finding>{error}</Finding> : null}
      </div>

      <StepActions
        primary="Continue"
        onPrimary={onNext}
        onBack={onBack}
        onSkip={onFinish}
        skipLabel={skipLabel}
      />
    </div>
  );
}

/**
 * A saved point, compact: its name, where it is, its area — and Edit/Delete.
 * The address is the map's reading of the pin (display only, never stored).
 */
function SavedPointRow({
  point,
  editingOther,
  onEdit,
  onDelete,
  deleting,
}: {
  point: api.AssemblyPoint;
  /** Another point is open — Edit greys and stays put until it is saved. */
  editingOther: boolean;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const indoor = point.kind === 'indoor_shelter';
  const address = usePinAddress(indoor ? null : point);
  const area = indoor
    ? 'Inside the building'
    : point.radius_m !== null
      ? `Circle, ${point.radius_m * 2} m across`
      : point.area_polygon !== null
        ? `Drawn area, ${point.area_polygon.length} corners`
        : 'No area';
  const detail = [point.is_default ? 'Default' : null, address, area].filter(Boolean).join(' · ');
  return (
    <li>
      <GroupRow
        detail={detail}
        actions={
          <>
            <Button
              variant="quiet"
              disabled={editingOther}
              label={`Edit ${point.name}`}
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              variant="quiet"
              disabled={deleting}
              label={`Delete ${point.name}`}
              onClick={onDelete}
            >
              Delete
            </Button>
          </>
        }
      >
        {point.name}
      </GroupRow>
    </li>
  );
}

/** The street address a pin reads as, looked up once per spot and remembered. */
const pinAddresses = new globalThis.Map<string, string | null>();
function usePinAddress(at: api.LatLon | null): string | null {
  const key = at ? `${at.latitude.toFixed(6)},${at.longitude.toFixed(6)}` : '';
  const [text, setText] = useState<string | null>(() => pinAddresses.get(key) ?? null);
  useEffect(() => {
    if (!at) return;
    if (pinAddresses.has(key)) {
      setText(pinAddresses.get(key) ?? null);
      return;
    }
    let alive = true;
    void reverseAddress(at.latitude, at.longitude).then((t) => {
      pinAddresses.set(key, t);
      if (alive) setText(t);
    });
    return () => {
      alive = false;
    };
  }, [key]);
  return text;
}

/**
 * The one point being added or edited, as the numbered questions and a Save,
 * inside its kind's group. The map is the other half of it: the pin, its
 * circle and ↔ handle, or the outline's corners are drawn live from the draft.
 */
function PointEditor({
  draft,
  section,
  onDraft,
  onPlaceByAddress,
  addressRegion,
  onSave,
  saving,
  onCancel,
  buildingPlaced,
}: {
  draft: PointDraft;
  section: (typeof ASSEMBLY_SECTIONS)[number];
  onDraft: (change: Partial<PointDraft>) => void;
  onPlaceByAddress: (at: api.LatLon) => void;
  addressRegion: SearchRegion | null;
  onSave: () => void;
  saving: boolean;
  onCancel: () => void;
  buildingPlaced: boolean;
}) {
  const indoor = draft.kind === 'indoor_shelter';
  // "Area across" is a DIAMETER typed or slid; the draft holds the radius.
  // The text is kept here so a half-typed number is never rewritten under the
  // fingers, and it follows the ↔ handle when that is dragged on the map.
  const [across, setAcross] = useState(String(draft.radiusM * 2));
  useEffect(() => {
    if (diameterToRadius(across) !== draft.radiusM) setAcross(String(draft.radiusM * 2));
  }, [draft.radiusM]);

  const outlineReady = draft.area !== 'outline' || draft.ring.length >= 3;
  const missing = !draft.name.trim()
    ? 'Name it to save it.'
    : !draft.at
      ? indoor
        ? 'Tap the map where it is to save it.'
        : 'Place it — type the address or tap the map — to save it.'
      : !outlineReady
        ? 'An outline needs at least three corners.'
        : null;
  const areaChoices: { value: PointDraft['area']; label: string }[] = [
    ...(draft.kind === 'reunification' ? [{ value: 'none' as const, label: 'No area' }] : []),
    { value: 'circle', label: 'Circle' },
    { value: 'outline', label: 'Draw an outline' },
  ];
  const editing = draft.id !== null;

  return (
    <div
      role="group"
      aria-label={editing ? `Editing ${draft.name || section.newLabel}` : section.newLabel}
      className="space-y-3 px-3 pt-2 pb-3"
    >
      <FieldInput
        label={indoor ? '1 · Where inside the building?' : '1 · Name'}
        value={draft.name}
        onChange={(v) => onDraft({ name: v })}
        placeholder={`e.g. ${section.placeholder}`}
        autoFocus={!editing}
      />

      {indoor ? (
        <Help live>
          {draft.at
            ? buildingPlaced
              ? 'Its pin starts on the building — drag it to the spot inside.'
              : 'Drag the pin to the spot inside the building.'
            : 'The building is not on the map yet — tap the map where this point is.'}
        </Help>
      ) : (
        <div>
          <AddressAutocomplete
            appearance="field"
            label="2 · Address"
            placeholder="Type an address"
            value={draft.address}
            onChange={(v) => onDraft({ address: v })}
            onPick={(c) => {
              if (c) onPlaceByAddress(c);
            }}
            region={addressRegion}
            preview={false}
          />
          <Help live>
            {draft.at
              ? 'On the map — drag the pin to adjust it, the address follows.'
              : 'Or tap the map where it is.'}
          </Help>
        </div>
      )}

      {indoor ? null : (
        <div>
          <Segmented
            name="point-area"
            legend="3 · Area"
            value={draft.area}
            options={areaChoices}
            onChange={(area) => onDraft({ area })}
          />
          {draft.area === 'circle' ? (
            <div className="mt-3">
              <AreaAcross
                label="Area across (m)"
                value={across}
                onChange={(v) => {
                  setAcross(v);
                  const r = diameterToRadius(v);
                  if (r !== undefined) onDraft({ radiusM: r });
                }}
                max={200}
              />
              <Help>Or drag the ↔ handle on the circle&rsquo;s edge.</Help>
            </div>
          ) : draft.area === 'outline' ? (
            <div>
              <Help live>
                {!draft.at
                  ? 'Place the point first, then tap the map at each corner of the area.'
                  : `Tap the map at each corner of the area — ${draft.ring.length} ${
                      draft.ring.length === 1 ? 'corner' : 'corners'
                    } so far.${draft.ring.length < 3 ? ' Three at least.' : ''}`}
              </Help>
              {draft.ring.length > 0 ? (
                <div className="mt-1 flex gap-2">
                  <Button
                    variant="quiet"
                    onClick={() => onDraft({ ring: draft.ring.slice(0, -1) })}
                  >
                    Undo last corner
                  </Button>
                  <Button variant="quiet" onClick={() => onDraft({ ring: [] })}>
                    Start again
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {draft.kind === 'outdoor_assembly' ? (
            // Built behaviour (migration 0075 + the app's arrival geofence,
            // #498) — said here because this is where the area is chosen.
            <Help>
              Anyone with the EVAC app is marked safe when their phone enters this area during an
              evacuation.
            </Help>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" disabled={missing !== null || saving} onClick={onSave}>
          {saving ? 'Saving…' : 'Save point'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        {missing ? (
          <span className="text-[12px] leading-[16px] text-[var(--ink-2)]">{missing}</span>
        ) : null}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- step 3 */

/**
 * Areas / zones — the spaces a warden reports clear. Reuses `addZone` /
 * `getZones` / `removeZone`, the same calls the setup flow's zone step makes.
 * Skippable; without areas a sweep is impossible and readiness says so.
 */
function ZonesStep({
  embedded,
  orgId,
  siteId,
  onNext,
  onBack,
  onFinish,
  skipLabel,
}: {
  embedded: boolean;
  orgId: string;
  siteId: string;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  skipLabel: string;
}) {
  const qc = useQueryClient();
  const zones = useQuery({
    queryKey: ['zones', orgId, siteId],
    queryFn: () => api.getZones(orgId, siteId),
  });
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] });

  // Both mutations surface their failure — a silent "Add"/"Remove" that does
  // nothing (the name field not clearing, the row not going) reads as the app
  // being broken. The assembly step already does this; matching it keeps the
  // wizard internally consistent (board MEDIUM).
  const add = useMutation({
    mutationFn: (n: string) => api.addZone(orgId, siteId, n),
    onSuccess: () => {
      setName('');
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.removeZone(orgId, siteId, id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const addNow = () => {
    if (name.trim() && !add.isPending) add.mutate(name.trim());
  };

  const list = zones.data?.zones ?? [];

  return (
    <div>
      <StepHeading embedded={embedded}>What are the areas?</StepHeading>

      <div className="mt-5 space-y-3">
        {/* The field comes FIRST and is ready to type into — an empty state
            that says "No areas yet" over a dashed box adds a sentence where an
            input would do (owner direction 2026-09-21). The list grows
            underneath as areas are added. */}
        <div>
          <FieldWithAction
            field={
              <FieldInput
                label="Add an area"
                value={name}
                onChange={setName}
                placeholder="Whole building"
                autoFocus
                onEnter={addNow}
              />
            }
            action={
              <Button variant="secondary" disabled={!name.trim() || add.isPending} onClick={addNow}>
                Add
              </Button>
            }
          />
          <Help>
            The floors, wings or zones a warden sweeps and reports clear. A small place can be one
            area — &ldquo;Whole building&rdquo; is a fine answer.
          </Help>
        </div>

        {zones.isLoading ? (
          <Spinner />
        ) : list.length > 0 ? (
          <FieldGroup label="Areas" listLabel="Areas">
            {list.map((z) => (
              <li key={z.id}>
                <GroupRow
                  detail={`${z.wardens_assigned} ${z.wardens_assigned === 1 ? 'warden' : 'wardens'}`}
                  actions={
                    <Button
                      variant="quiet"
                      label={`Remove ${z.name}`}
                      onClick={() => remove.mutate(z.id)}
                    >
                      Remove
                    </Button>
                  }
                >
                  {z.name}
                </GroupRow>
              </li>
            ))}
          </FieldGroup>
        ) : null}

        {error ? <Finding>{error}</Finding> : null}
      </div>

      <StepActions
        primary="Continue"
        onPrimary={onNext}
        onBack={onBack}
        onSkip={onFinish}
        skipLabel={skipLabel}
      />
    </div>
  );
}

/* --------------------------------------------------------------------- step 4 */

/** The warden-ish roles a zone assignment may hold — matching what the ECO
 *  board files per area, so an assignment made either place reads the same. */
const ZONE_WARDEN_ROLES = new Set(['area_warden', 'floor_warden', 'warden']);

/**
 * Wardens on areas — the AS 3745 control organisation, zone-first: the Chief
 * Warden, then every area as a field with a person picker, then the way to
 * invite somebody who is not on the team yet, then who can raise a real alert
 * here. Skippable; a solo café owner is implicitly Chief and needs none.
 */
function WardensStep({
  embedded,
  orgId,
  siteId,
  onNext,
  onBack,
  onFinish,
  skipLabel,
}: {
  embedded: boolean;
  orgId: string;
  siteId: string;
  onNext: () => void;
  onBack: () => void;
  onFinish: () => void;
  skipLabel: string;
}) {
  const qc = useQueryClient();
  const zones = useQuery({
    queryKey: ['zones', orgId, siteId],
    queryFn: () => api.getZones(orgId, siteId),
  });
  const eco = useQuery({
    queryKey: ['eco', orgId, siteId],
    queryFn: () => api.getEco(orgId, siteId),
  });
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  // The building row carries the activation rule and whether it was chosen.
  const sites = useQuery({ queryKey: ['sites', orgId], queryFn: () => api.getSites(orgId) });
  const site = sites.data?.sites.find((s) => s.id === siteId);

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const refreshSite = () => {
    void qc.invalidateQueries({ queryKey: ['sites', orgId] });
    void qc.invalidateQueries({ queryKey: ['readiness', orgId, siteId] });
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['eco', orgId, siteId] });
    void qc.invalidateQueries({ queryKey: ['zones', orgId, siteId] });
  };

  const assign = useMutation({
    mutationFn: (body: { userId: string; role: string; zoneId?: string }) =>
      api.assignEco(orgId, siteId, body),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const vacate = useMutation({
    mutationFn: (assignmentId: string) => api.vacateEco(orgId, siteId, assignmentId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  // "If the warden doesn't exist, they can create one" — creating a person IS
  // inviting them (there is no shareable join link; the server mints a token
  // for one named address). Honest about the seam: an invitee has no
  // membership until they accept, so they appear in the pickers then, not now.
  const [inviteEmail, setInviteEmail] = useState('');
  const invite = useMutation({
    mutationFn: () => api.invitePerson(orgId, inviteEmail.trim(), 'staff'),
    onSuccess: (res) => {
      setInviteEmail('');
      setError(null);
      setNote(
        res?.status === 'already_a_member'
          ? 'That person is already on the team — pick them above.'
          : 'Invitation sent. They appear in the pickers the moment they accept.',
      );
    },
    onError: (e: Error) => setError(e.message),
  });
  const inviteNow = () => {
    if (inviteEmail.trim() && !invite.isPending) invite.mutate();
  };

  const members = (roster.data?.members ?? []).filter((m) => m.deprovisioned_at === null);
  const memberOptions = [
    { value: '', label: 'Choose a person…' },
    ...members.map((m) => ({ value: m.user_id, label: m.display_name ?? m.email ?? 'Unnamed' })),
  ];
  const assignments = eco.data?.assignments ?? [];
  const chief = assignments.find((a) => a.role === 'chief_warden' && a.display_name);
  const zoneList = zones.data?.zones ?? [];
  const wardenFor = (zoneName: string) =>
    assignments.find(
      (a) => ZONE_WARDEN_ROLES.has(a.role) && a.zone_name === zoneName && a.display_name,
    );

  const loading = zones.isLoading || eco.isLoading || roster.isLoading;

  return (
    <div>
      <StepHeading embedded={embedded}>Who sweeps where?</StepHeading>

      <div className="mt-5 space-y-3">
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* The Chief Warden — the one person everyone knows is in charge. */}
            <div>
              {chief ? (
                <HolderField
                  label="Chief Warden"
                  name={chief.display_name ?? ''}
                  pending={vacate.isPending}
                  vacateLabel={`Vacate ${chief.display_name ?? 'the Chief Warden'} as Chief Warden`}
                  onVacate={() => vacate.mutate(chief.id)}
                />
              ) : (
                <ChoosePerson
                  label="Chief Warden"
                  options={memberOptions}
                  pending={assign.isPending}
                  onAssign={(userId) => assign.mutate({ userId, role: 'chief_warden' })}
                />
              )}
              <Help>Runs the response for the whole building.</Help>
            </div>

            {/* Every area as a field: its warden, or the picker to name one. */}
            {zoneList.length === 0 ? (
              <Help>
                No areas yet — go back a step and add them; a warden is assigned per area.
              </Help>
            ) : (
              <ul className="m-0 list-none space-y-3 p-0" aria-label="Area wardens">
                {zoneList.map((z) => {
                  const holder = wardenFor(z.name);
                  return (
                    <li key={z.id}>
                      {holder ? (
                        <HolderField
                          label={z.name}
                          name={holder.display_name ?? ''}
                          pending={vacate.isPending}
                          vacateLabel={`Vacate ${holder.display_name ?? 'the warden'} from ${z.name}`}
                          onVacate={() => vacate.mutate(holder.id)}
                        />
                      ) : (
                        <ChoosePerson
                          label={z.name}
                          options={memberOptions}
                          pending={assign.isPending}
                          onAssign={(userId) =>
                            assign.mutate({ userId, role: 'area_warden', zoneId: z.id })
                          }
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Somebody not on the team yet: one named address, declinable in a tap. */}
            <div>
              <FieldWithAction
                field={
                  <FieldInput
                    label="Invite a warden by email"
                    value={inviteEmail}
                    onChange={setInviteEmail}
                    type="email"
                    inputMode="email"
                    placeholder="name@school.edu"
                    onEnter={inviteNow}
                  />
                }
                action={
                  <Button
                    variant="secondary"
                    disabled={!inviteEmail.trim() || invite.isPending}
                    onClick={inviteNow}
                  >
                    {invite.isPending ? 'Inviting…' : 'Invite'}
                  </Button>
                }
              />
              <Help>They appear in the pickers the moment they accept.</Help>
            </div>
          </>
        )}

        {note ? <Finding tone="neutral">{note}</Finding> : null}
        {error ? <Finding>{error}</Finding> : null}

        {/* Who can raise a real alert here — with the wardens, not the address
            (product owner, 2026-09-30). The SAME control as the building page's
            Wardens section, so tighten / ask-to-loosen / second-administrator
            behaviour is one implementation. Skipping it leaves the building on
            one administrator, unchosen, and readiness asks. */}
        <ActivationRuleSetting orgId={orgId} siteId={siteId} site={site} onSaved={refreshSite} />
      </div>

      <StepActions
        primary="Continue"
        onPrimary={onNext}
        onBack={onBack}
        onSkip={onFinish}
        skipLabel={skipLabel}
      />
    </div>
  );
}

/** A person picker + Assign — the control the Chief Warden and each area use,
 *  so assigning reads the same everywhere. The label names the post or area. */
function ChoosePerson({
  label,
  options,
  pending,
  onAssign,
}: {
  label: string;
  options: { value: string; label: string }[];
  pending: boolean;
  onAssign: (userId: string) => void;
}) {
  const [userId, setUserId] = useState('');
  return (
    <FieldWithAction
      field={<FieldSelect label={label} value={userId} onChange={setUserId} options={options} />}
      action={
        <Button
          variant="secondary"
          disabled={!userId || pending}
          onClick={() => {
            onAssign(userId);
            setUserId('');
          }}
        >
          Assign
        </Button>
      }
    />
  );
}

/** A post or area with its holder named, and the way to vacate it. */
function HolderField({
  label,
  name,
  pending,
  vacateLabel,
  onVacate,
}: {
  label: string;
  name: string;
  pending: boolean;
  vacateLabel: string;
  onVacate: () => void;
}) {
  return (
    <FieldWithAction
      field={<FieldStatic label={label}>{name}</FieldStatic>}
      action={
        <Button variant="quiet" disabled={pending} label={vacateLabel} onClick={onVacate}>
          Vacate
        </Button>
      }
    />
  );
}

/* --------------------------------------------------------------------- step 5 */

/**
 * Who is alerted here? — the audience step (owner direction 2026-09-21: "the
 * last section, people, should only be people who should be notified when an
 * alert is activated — wardens are done elsewhere, admins on the first
 * screen"). It rides teams-as-audience (migration 0113): switch on the team(s)
 * whose members an activation on this building reaches; with no team on, an
 * alert reaches the whole organisation. Wardens and administrators are always
 * included, whatever the teams — a warden must never miss their own alert.
 *
 * The count is the server's `audience-preview` — the SAME predicates the send
 * uses — never a client-side roster count, and the step says out loud how many
 * people the linked teams would EXCLUDE, because a narrowed audience quietly
 * assumed to be "everyone" is how somebody is never told the building is on
 * fire. The last step, so its primary finishes the wizard.
 */
function AlertedStep({
  embedded,
  orgId,
  siteId,
  onBack,
  onFinish,
  finishLabel,
}: {
  embedded: boolean;
  orgId: string;
  siteId: string;
  onBack: () => void;
  onFinish: () => void;
  finishLabel: string;
}) {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams', orgId], queryFn: () => api.getTeams(orgId) });
  const roster = useQuery({ queryKey: ['roster', orgId], queryFn: () => api.getRoster(orgId) });
  const preview = useQuery({
    queryKey: ['audience-preview', orgId, siteId],
    queryFn: () => api.audiencePreview(orgId, siteId),
  });

  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['teams', orgId] });
    void qc.invalidateQueries({ queryKey: ['audience-preview', orgId, siteId] });
  };

  const link = useMutation({
    mutationFn: (teamId: string) => api.addTeamSite(orgId, teamId, siteId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const unlink = useMutation({
    mutationFn: (teamId: string) => api.removeTeamSite(orgId, teamId, siteId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const [newTeam, setNewTeam] = useState('');
  const create = useMutation({
    // Create-and-link as one act: a team made on THIS step exists to be this
    // building's audience, so it never lands unlinked and silently inert.
    mutationFn: async () => {
      const res = await api.createTeam(orgId, newTeam.trim());
      if (res?.id) await api.addTeamSite(orgId, res.id, siteId);
    },
    onSuccess: () => {
      setNewTeam('');
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const createNow = () => {
    if (newTeam.trim() && !create.isPending) create.mutate();
  };

  // Invite a person INTO a team (migration 0112) — the membership is applied
  // when they accept, so the copy says so rather than pretending it is done.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteTeamId, setInviteTeamId] = useState('');
  const invite = useMutation({
    mutationFn: () =>
      api.invitePerson(orgId, inviteEmail.trim(), 'staff', inviteTeamId || undefined),
    onSuccess: (res) => {
      setInviteEmail('');
      setError(null);
      setNote(
        res?.status === 'already_a_member'
          ? 'That person is already in the organisation — add them to a team from the People screen.'
          : 'Invitation sent. They join the team — and are alerted here — the moment they accept.',
      );
    },
    onError: (e: Error) => setError(e.message),
  });
  const canInvite = Boolean(inviteEmail.trim()) && Boolean(inviteTeamId) && !invite.isPending;

  const teamList = teams.data?.teams ?? [];
  const linked = new Set(
    teamList.filter((t) => t.sites.some((s) => s.id === siteId)).map((t) => t.id),
  );
  const activeMembers = (roster.data?.members ?? []).filter(
    (m) => m.deprovisioned_at === null,
  ).length;
  const count = preview.data?.count;
  const basis = preview.data?.basis;
  // How many people the linked teams leave OUT — only meaningful against the
  // roster denominator (a presence-based site answers a different question).
  const excluded =
    basis === 'team_roster' && count !== undefined ? Math.max(0, activeMembers - count) : 0;

  const loading = teams.isLoading || preview.isLoading;
  const busy = link.isPending || unlink.isPending;

  return (
    <div>
      <StepHeading embedded={embedded}>Who is alerted here?</StepHeading>

      <div className="mt-5 space-y-3">
        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* The live count — the server's answer, in one sentence. */}
            {count !== undefined ? (
              <div>
                <p className="m-0 text-[15px] leading-[20px] text-[var(--ink)]">
                  An alert here right now would reach {count} {count === 1 ? 'person' : 'people'}.
                </p>
                <Help>
                  {basis === 'presence'
                    ? 'Counted from who is signed in as present right now — presence decides the audience here, and teams do not change it.'
                    : basis === 'team_roster'
                      ? 'Counted from the teams linked to this building, plus its wardens and administrators.'
                      : 'Everyone in the organisation — no team is linked to this building yet.'}
                </Help>
                {excluded > 0 ? (
                  <div className="mt-2">
                    <Finding tone="attention">
                      {excluded} {excluded === 1 ? 'person' : 'people'} in the organisation would{' '}
                      <strong>not</strong> be alerted here — outside the linked teams, with no
                      warden or administrator role. Link their team, or switch every team off to
                      alert everyone.
                    </Finding>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* The teams, each with a switch: on = alerted on this building. */}
            <div>
              <FieldGroup label="Teams" listLabel="Teams alerted on this building">
                {teamList.length === 0 ? (
                  <li className="px-3 py-2 text-[14px] leading-[18px] text-[var(--ink-2)]">
                    No teams yet — an alert here reaches everyone in the organisation.
                  </li>
                ) : (
                  teamList.map((t) => {
                    const isLinked = linked.has(t.id);
                    return (
                      <li key={t.id}>
                        <Switch
                          checked={isLinked}
                          disabled={busy}
                          ariaLabel={`Alert ${t.name} on this building`}
                          describedBy={`team-state-${t.id}`}
                          onChange={() => (isLinked ? unlink.mutate(t.id) : link.mutate(t.id))}
                        >
                          <span
                            className="block truncate text-[14px] leading-[18px]"
                            title={t.name}
                          >
                            {t.name}
                          </span>
                          <span
                            id={`team-state-${t.id}`}
                            className="block text-[12px] leading-[15px] text-[var(--ink-2)]"
                          >
                            {t.memberCount} {t.memberCount === 1 ? 'member' : 'members'} ·{' '}
                            {isLinked ? 'Alerted here' : 'Not alerted here'}
                          </span>
                        </Switch>
                      </li>
                    );
                  })
                )}
              </FieldGroup>
              <Help>
                With no team switched on, an alert reaches everyone in the organisation. Wardens and
                administrators are always alerted.
              </Help>
            </div>

            {/* A team that does not exist yet: made and switched on in one act. */}
            <FieldWithAction
              field={
                <FieldInput
                  label="New team"
                  value={newTeam}
                  onChange={setNewTeam}
                  placeholder="Reception"
                  onEnter={createNow}
                />
              }
              action={
                <Button
                  variant="secondary"
                  disabled={!newTeam.trim() || create.isPending}
                  onClick={createNow}
                >
                  {create.isPending ? 'Creating…' : 'Create and alert'}
                </Button>
              }
            />

            {/* Somebody into a team: they join it — and are alerted here — on accept. */}
            {teamList.length > 0 ? (
              <div className="space-y-3">
                <FieldInput
                  label="Invite by email"
                  value={inviteEmail}
                  onChange={setInviteEmail}
                  type="email"
                  inputMode="email"
                  placeholder="name@school.edu"
                />
                <div>
                  <FieldWithAction
                    field={
                      <FieldSelect
                        label="Team"
                        value={inviteTeamId}
                        onChange={setInviteTeamId}
                        options={[
                          { value: '', label: 'Choose a team…' },
                          ...teamList.map((t) => ({ value: t.id, label: t.name })),
                        ]}
                      />
                    }
                    action={
                      <Button
                        variant="secondary"
                        disabled={!canInvite}
                        onClick={() => invite.mutate()}
                      >
                        {invite.isPending ? 'Inviting…' : 'Invite'}
                      </Button>
                    }
                  />
                  <Help>They join the team — and are alerted here — when they accept.</Help>
                </div>
              </div>
            ) : null}
          </>
        )}

        {note ? <Finding tone="neutral">{note}</Finding> : null}
        {error ? <Finding>{error}</Finding> : null}
      </div>

      <StepActions primary={finishLabel} onPrimary={onFinish} onBack={onBack} />
    </div>
  );
}
